import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions } from '../../../../../adapters/types'
import { DischargeFormData } from '../../../../types/formData'
import { DischargeFormField } from '../../../../types/formField'
import { DischargeAssessmentQuestionnaireItem } from '../../../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../../../helpers'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { FlatAnswerMutation } from '../../../../../adapters/types'

/**
 * Gastrointestinal subsection adapter
 * Handles the most complex GI/GU assessment with feeding tubes, catheters, nutrition (19 fields)
 * This is the most complex subsection with many interdependent fields
 */
export class GastrointestinalAdapter extends BaseSubsectionAdapter<DischargeFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<DischargeFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // GI assessment
    const giAssessment = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GI_ASSESSMENT
    )

    const bmDate = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_BM_DATE
    )?.at(0)

    // GU assessment
    const guAssessment = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GU_ASSESSMENT
    )

    // Catheter assessment
    const catheter = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_CATHETER
    )?.at(0)

    const catheterType = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_CATHETER_TYPE
    )

    // Feeding tube assessment - complex with many related fields
    const feedingTube = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE
    )?.at(0)

    const feedingTubeAmount = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_AMOUNT
    )?.at(0)

    const feedingTotalNumber = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TOTAL_NUMBER
    )?.at(0)

    const feedingTubeDuration = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_DURATION
    )?.at(0)

    const feedingTubeType = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_TYPE
    )?.at(0)

    const feedingTubeSite = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_SITE
    )

    const feedingTubeSize = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_SIZE
    )?.at(0)

    const feedingTubeNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_NOTES
    )?.at(0)

    // Nutrition assessment
    const nutritionWnl = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITION_WNL
    )?.at(0)

    const nutritionalFactors = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITIONAL_FACTORS
    )

    const dietRequirements = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_DIET_REQUIREMENTS
    )?.at(0)

    const nutritionNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITION_NOTES
    )?.at(0)

    const nutritionalScreening = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITIONAL_SCREENING
    )

    const historyNotes = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GASTROINTESTINAL_HISTORY_NOTES
    )?.at(0)

    const systemSummary = getAnswerValuesFromItems(
      flatAnswersArray,
      DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_SYSTEM_SUMMARY
    )?.at(0)

    return {
      [DischargeFormField.GASTROINTESTINAL_GI_ASSESSMENT]: giAssessment,
      [DischargeFormField.GASTROINTESTINAL_BM_DATE]: bmDate,
      [DischargeFormField.GASTROINTESTINAL_GU_ASSESSMENT]: guAssessment,
      [DischargeFormField.GASTROINTESTINAL_CATHETER]: catheter,
      [DischargeFormField.GASTROINTESTINAL_CATHETER_TYPE]: catheterType,
      [DischargeFormField.GASTROINTESTINAL_FEEDING_TUBE]: feedingTube,
      [DischargeFormField.GASTROINTESTINAL_FEEDING_TUBE_AMOUNT]: feedingTubeAmount as number,
      [DischargeFormField.GASTROINTESTINAL_FEEDING_TOTAL_NUMBER]: feedingTotalNumber as number,
      [DischargeFormField.GASTROINTESTINAL_FEEDING_TUBE_DURATION]: feedingTubeDuration,
      [DischargeFormField.GASTROINTESTINAL_FEEDING_TUBE_TYPE]: feedingTubeType,
      [DischargeFormField.GASTROINTESTINAL_FEEDING_TUBE_SITE]: feedingTubeSite,
      [DischargeFormField.GASTROINTESTINAL_FEEDING_TUBE_SIZE]: feedingTubeSize,
      [DischargeFormField.GASTROINTESTINAL_FEEDING_TUBE_NOTES]: feedingTubeNotes,
      [DischargeFormField.GASTROINTESTINAL_NUTRITION_WNL]: nutritionWnl,
      [DischargeFormField.GASTROINTESTINAL_NUTRITIONAL_FACTORS]: nutritionalFactors,
      [DischargeFormField.GASTROINTESTINAL_DIET_REQUIREMENTS]: dietRequirements,
      [DischargeFormField.GASTROINTESTINAL_NUTRITION_NOTES]: nutritionNotes,
      [DischargeFormField.GASTROINTESTINAL_NUTRITIONAL_SCREENING]: nutritionalScreening,
      [DischargeFormField.GASTROINTESTINAL_HISTORY_NOTES]: historyNotes,
      [DischargeFormField.GASTROINTESTINAL_SYSTEM_SUMMARY]: systemSummary
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(DischargeFormField.GASTROINTESTINAL_GI_ASSESSMENT)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GI_ASSESSMENT,
        value: this.getFormDataField(DischargeFormField.GASTROINTESTINAL_GI_ASSESSMENT)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.GASTROINTESTINAL_BM_DATE)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_BM_DATE,
        value: this.getFormDataField(DischargeFormField.GASTROINTESTINAL_BM_DATE)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.GASTROINTESTINAL_GU_ASSESSMENT)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GU_ASSESSMENT,
        value: this.getFormDataField(DischargeFormField.GASTROINTESTINAL_GU_ASSESSMENT)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.GASTROINTESTINAL_CATHETER)) {
      mutations.push({
        linkId: DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_CATHETER,
        value: this.getFormDataField(DischargeFormField.GASTROINTESTINAL_CATHETER)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.GASTROINTESTINAL_CATHETER_TYPE)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_CATHETER_TYPE,
        value: this.getFormDataField(DischargeFormField.GASTROINTESTINAL_CATHETER_TYPE)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.GASTROINTESTINAL_FEEDING_TUBE)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE,
        value: this.getFormDataField(DischargeFormField.GASTROINTESTINAL_FEEDING_TUBE)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.GASTROINTESTINAL_FEEDING_TUBE_AMOUNT)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_AMOUNT,
        value: this.getFormDataField(DischargeFormField.GASTROINTESTINAL_FEEDING_TUBE_AMOUNT)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.GASTROINTESTINAL_FEEDING_TOTAL_NUMBER)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TOTAL_NUMBER,
        value: this.getFormDataField(DischargeFormField.GASTROINTESTINAL_FEEDING_TOTAL_NUMBER)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.GASTROINTESTINAL_FEEDING_TUBE_DURATION)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_DURATION,
        value: this.getFormDataField(DischargeFormField.GASTROINTESTINAL_FEEDING_TUBE_DURATION)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.GASTROINTESTINAL_FEEDING_TUBE_TYPE)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_TYPE,
        value: this.getFormDataField(DischargeFormField.GASTROINTESTINAL_FEEDING_TUBE_TYPE)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.GASTROINTESTINAL_FEEDING_TUBE_SITE)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_SITE,
        value: this.getFormDataField(DischargeFormField.GASTROINTESTINAL_FEEDING_TUBE_SITE)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.GASTROINTESTINAL_FEEDING_TUBE_SIZE)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_SIZE,
        value: this.getFormDataField(DischargeFormField.GASTROINTESTINAL_FEEDING_TUBE_SIZE)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.GASTROINTESTINAL_FEEDING_TUBE_NOTES)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_FEEDING_TUBE_NOTES,
        value: this.getFormDataField(DischargeFormField.GASTROINTESTINAL_FEEDING_TUBE_NOTES)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.GASTROINTESTINAL_NUTRITION_WNL)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITION_WNL,
        value: this.getFormDataField(DischargeFormField.GASTROINTESTINAL_NUTRITION_WNL)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.GASTROINTESTINAL_NUTRITIONAL_FACTORS)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITIONAL_FACTORS,
        value: this.getFormDataField(DischargeFormField.GASTROINTESTINAL_NUTRITIONAL_FACTORS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.GASTROINTESTINAL_DIET_REQUIREMENTS)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_DIET_REQUIREMENTS,
        value: this.getFormDataField(DischargeFormField.GASTROINTESTINAL_DIET_REQUIREMENTS)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.GASTROINTESTINAL_NUTRITION_NOTES)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITION_NOTES,
        value: this.getFormDataField(DischargeFormField.GASTROINTESTINAL_NUTRITION_NOTES)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.GASTROINTESTINAL_NUTRITIONAL_SCREENING)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_NUTRITIONAL_SCREENING,
        value: this.getFormDataField(DischargeFormField.GASTROINTESTINAL_NUTRITIONAL_SCREENING)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.GASTROINTESTINAL_HISTORY_NOTES)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_GASTROINTESTINAL_HISTORY_NOTES,
        value: this.getFormDataField(DischargeFormField.GASTROINTESTINAL_HISTORY_NOTES)
      })
    }
    if (this.formDataFieldExists(DischargeFormField.GASTROINTESTINAL_SYSTEM_SUMMARY)) {
      mutations.push({
        linkId:
          DischargeAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_GASTROINTESTINAL_SYSTEM_SUMMARY,
        value: this.getFormDataField(DischargeFormField.GASTROINTESTINAL_SYSTEM_SUMMARY)
      })
    }

    return mutations
  }
}
